import { useEffect, useState } from "react";
import { Scale, Dna, ExternalLink, TriangleAlert } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { PageHero } from "@/components/site/PageHero";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConversionTool } from "@/components/tools/ConversionTool";
import { CrossTool } from "@/components/tools/CrossTool";

const TOOLS = [
  ["conversion", "Conversión y ganancia", Scale, ConversionTool, "Calcule días a terminación, conversión alimenticia y margen, comparando su rodeo actual con genética Brangus.", [
    ["Consumo de materia seca: entre 1,8% y 2,7% del peso vivo según la calidad del forraje. Mississippi State University Extension, Beef Cattle Nutrient Requirements.", "https://extension.msstate.edu/publications/beef-cattle-nutrient-requirements"],
    ["Mejora del crecimiento en cruzas cebú × Angus por heterosis (≈12,6% individual en peso al destete). Hansen, University of Florida IFAS, AN165.", "https://journals.flvc.org/edis/article/download/116132/114304"],
  ], "Las ganancias diarias, costos y precios son valores de referencia que usted debe reemplazar por los de su finca. La ganancia «con Brangus» es una estimación, no un resultado medido en su rodeo."],
  ["cruzamiento", "Simulador de cruzamiento", Dna, CrossTool, "Elija el vientre y el padre: vea la composición racial de la cría, la heterosis y la mejora esperada.", [
    ["Heterosis cebú × taurino: 12,6% individual y 16% materna en peso al destete, frente a 3,9% entre razas taurinas. Hansen, University of Florida IFAS, Crossbreeding Systems in Beef Cattle (AN165).", "https://journals.flvc.org/edis/article/download/116132/114304"],
    ["Sistemas de cruzamiento y retención de heterosis. Mississippi State University Extension, Crossbreeding Systems for Beef Cattle (P2755).", "https://extension.msstate.edu/publications/crossbreeding-systems-for-beef-cattle"],
    ["Heterosis y desempeño de cruzas Brahman × Angus. USDA Agricultural Research Service.", "https://www.ars.usda.gov/research/publications/publication/?seqNo115=397522"],
  ], "La composición racial y la heterosis se calculan con la fórmula genética estándar. Los kilos y meses son proyecciones a partir de valores de referencia generales, no de mediciones de animales concretos."],
];

const Sources = ({ id, sources, basis }) => (
  <div className="mt-14 grid gap-6 lg:grid-cols-2" data-testid={`tool-sources-${id}`}>
    <div>
      <p className="eyebrow">Base de los cálculos</p>
      <p className="mt-3 text-sm text-[#5C635E] leading-relaxed">{basis}</p>
      <ul className="mt-4 space-y-3 text-sm">
        {sources.map(([text, url], i) => (
          <li key={url}><a href={url} target="_blank" rel="noreferrer" data-testid={`tool-source-${id}-${i}`} className="inline-flex gap-2 text-[#1D3324] hover:text-[#8B2B22] underline-offset-4 hover:underline">
            <ExternalLink size={14} className="mt-1 shrink-0 text-[#8B2B22]" />{text}</a></li>
        ))}
      </ul>
    </div>
    <div className="border-l-4 border-[#C5A059] bg-[#F3EAD7]/70 p-6 text-sm text-[#1D3324] leading-relaxed self-start" data-testid={`tool-disclaimer-${id}`}>
      <p className="flex items-center gap-2 font-semibold"><TriangleAlert size={16} className="text-[#8B2B22]" />Aviso importante</p>
      <p className="mt-2">Los resultados son aproximados y orientativos. Pueden variar mucho según los animales elegidos (su genética individual y sus DEPs), las condiciones ambientales, la nutrición, la sanidad y el manejo de cada finca. No reemplazan la evaluación de un profesional.</p>
    </div>
  </div>
);

export default function Herramientas() {
  const [params, setParams] = useState(null);
  const [cover, setCover] = useState(null);
  useEffect(() => {
    api.get("/tools/params").then((r) => setParams(r.data));
    api.get("/media").then((r) => setCover(r.data.find((m) => m.type === "image" && /Rusticidad|Luquense/.test(m.title))));
  }, []);

  return (
    <div data-testid="herramientas-page">
      <PageHero testid="herramientas-hero" eyebrow="Herramientas" title="Herramientas para el productor" image={fileUrl(cover?.url)}
        text="Calculadoras simples para estimar el impacto de la genética en su producción." />
      <section className="container-fv py-16 sm:py-24">
        {params && (
          <Tabs defaultValue="conversion">
            <TabsList className="h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
              {TOOLS.map(([v, label, Icon]) => (
                <TabsTrigger key={v} value={v} data-testid={`tool-tab-${v}`} className="gap-2 rounded-full border border-[#8B2B22]/20 px-5 py-2.5 data-[state=active]:bg-[#8B2B22] data-[state=active]:text-white">
                  <Icon size={16} />{label}
                </TabsTrigger>
              ))}
            </TabsList>
            {TOOLS.map(([v, label, , Tool, desc, sources, basis]) => (
              <TabsContent key={v} value={v} forceMount className="mt-10 data-[state=inactive]:hidden">
                <h2 className="f-display text-3xl">{label}</h2>
                <p className="mt-2 mb-10 max-w-2xl text-[#5C635E]">{desc}</p>
                <Tool params={params} />
                <Sources id={v} sources={sources} basis={basis} />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </section>
    </div>
  );
}
